import { Component, DestroyRef, inject, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, filter, finalize, map, of, switchMap, takeUntil, tap, throwIfEmpty } from 'rxjs';
import { FirebaseEventService } from '../../../authentication/firebase.event.service';
import { AuthenticationService } from '../../../authentication/authentication.service';
import { MceApiService } from '../../../core/mceapi.client.generated';
import { appOpt, getApiErrorMessage } from '../../../core/mceapi.client.helpers';
import { isSome } from '../../functions/functional';

export interface SharedBillingInvitationData {
  invitationUid: string;
  isCurrentSession: () => boolean;
}

@Component({
  selector: 'app-shared-billing-invitation',
  imports: [MatButtonModule, MatDialogModule, MatProgressSpinnerModule],
  templateUrl: './shared-billing-invitation.component.html',
})
export class SharedBillingInvitationComponent {
  private readonly api = inject(MceApiService);
  private readonly firebaseEventService = inject(FirebaseEventService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly dialogRef = inject(MatDialogRef<SharedBillingInvitationComponent>);
  private readonly data = inject<SharedBillingInvitationData>(MAT_DIALOG_DATA);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly busy = signal(false);
  protected readonly accepted = signal(false);
  protected readonly error = signal('');
  protected readonly owner = rxResource({
    stream: () => this.api.v1SharedBillingOwner().pipe(
      map(dto => {
        const owner = appOpt(dto);
        return isSome(owner) ? owner.value : undefined;
      }),
      catchError(() => of(undefined)),
    ),
  });

  protected respond(action: 'accept' | 'decline'): void {
    if (this.busy() || !this.data.isCurrentSession() || (action === 'decline' && this.accepted())) return;
    this.busy.set(true);
    this.dialogRef.disableClose = true;
    this.error.set('');
    const operation = action === 'decline'
      ? this.api.v1SharedBillingInvitationsDecline(this.data.invitationUid)
      : this.accepted() ? of(undefined) : this.api.v1SharedBillingInvitationsAccept(this.data.invitationUid);

    let closing = false;
    operation.pipe(
      filter(() => this.data.isCurrentSession()),
      tap(() => {
        if (action === 'accept') {
          if (this.accepted()) return;
          this.accepted.set(true);
          this.firebaseEventService.logSharedBillingInvitationAcceptedEvent(this.authenticationService.currentUser());
        } else {
          this.firebaseEventService.logSharedBillingInvitationDeclinedEvent(this.authenticationService.currentUser());
        }
      }),
      switchMap(() => action === 'accept' ? this.authenticationService.refreshCurrentUser(this.data.isCurrentSession) : of(undefined)),
      throwIfEmpty(() => new Error('The invitation request did not finish. Please try again.')),
      takeUntil(this.dialogRef.beforeClosed()),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        if (!closing) {
          this.busy.set(false);
          this.dialogRef.disableClose = false;
        }
      }),
    ).subscribe({
      next: () => {
        if (this.data.isCurrentSession()) {
          closing = true;
          this.dialogRef.close(action);
        }
      },
      error: error => {
        if (!this.data.isCurrentSession()) return;
        this.error.set(this.accepted()
          ? 'Your invitation was accepted, but your account details could not be refreshed. Please retry to finish.'
          : getApiErrorMessage(error));
      },
    });
  }
}
